import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, Box, TextField, Button, Divider, Alert, FormHelperText, Paper, Typography, IconButton, Checkbox, FormControlLabel } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

type ConnectedProviders = {
  google?: boolean;
  kakao?: boolean;
  naver?: boolean;
};

type SocialProvider = "google" | "kakao" | "naver";

interface AccountSettingsForm {
  phone: string;
  marketingEmailAgree: boolean;
  marketingSmsAgree: boolean;
}

interface AccountSettingsModalProps {
  open: boolean;
  onClose: () => void;

  userEmail: string;
  userPhone?: string;
  marketingEmailAgree?: boolean;
  marketingSmsAgree?: boolean;

  connectedProviders?: ConnectedProviders;

  loading?: boolean;
  error?: string | null;
  successMessage?: string | null;

  onSave?: (form: AccountSettingsForm) => void;
  onOpenPasswordChange?: () => void;
  onWithdraw?: () => void;
  onOpenEmailChange?: () => void;

  onConnectSocial?: (provider: SocialProvider) => void;
  onDisconnectSocial?: (provider: SocialProvider) => void;
  socialLoadingProvider?: SocialProvider | null;
}

/* ===== Tokens ===== */
const CARD_BG = "#0b1522";
const SURFACE = "#0e1a2b";
const BORDER = "rgba(120,140,180,.18)";
const SUB_BORDER = "rgba(120,140,180,.14)";
const TEXT_MAIN = "#eaf2ff";
const TEXT_SUB = "#9fb4d8";
const PRIMARY_GRAD = "linear-gradient(90deg,#3ec7ff,#447dff)";

/* ---------- Utils ---------- */
const safeTrim = (s: string) => (s ?? "").trim();
const normalizePhone = (raw: string) => raw.replace(/[^0-9]/g, "");
const isLikelyKRPhone = (raw: string) => /^01[0-9][0-9]{7,8}$/.test(normalizePhone(raw));

/* ---------- Styled ---------- */
const Card = styled(Paper)({
  borderRadius: 18,
  padding: 16,
  border: `1px solid ${BORDER}`,
  background: CARD_BG,
  boxShadow: "0 18px 40px rgba(8,20,40,.45)",
});

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: SURFACE,
    color: TEXT_MAIN,
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: "#7fb0ff" },
    "&.Mui-focused fieldset": {
      borderColor: "#7fb0ff",
      boxShadow: "0 0 0 3px rgba(127,176,255,.18)",
    },
    "& input": { color: TEXT_MAIN },
  },
  "& .MuiInputLabel-root": { color: TEXT_SUB },
  "& .MuiInputLabel-root.Mui-focused": { color: "#b8d8ff" },
  "& .MuiFormHelperText-root": { marginLeft: 0, color: TEXT_SUB },
};

const btnPrimary = {
  height: 44,
  borderRadius: 12,
  fontWeight: 900,
  textTransform: "none" as const,
  color: "#0b1522",
  background: PRIMARY_GRAD,
  border: "1px solid rgba(62,199,255,.4)",
  boxShadow: "0 8px 20px rgba(62,199,255,.25)",
  "&:hover": {
    filter: "brightness(1.05)",
    boxShadow: "0 10px 24px rgba(62,199,255,.28)",
  },
};

const btnOutline = {
  height: 44,
  borderRadius: 12,
  fontWeight: 800,
  textTransform: "none" as const,
  color: TEXT_MAIN,
  border: `1px solid ${BORDER}`,
  background: "transparent",
  "&:hover": {
    borderColor: "#7fb0ff",
    boxShadow: "0 6px 16px rgba(16,24,40,.25)",
  },
};

const sectionTitleSx = {
  color: TEXT_MAIN,
  fontWeight: 900,
  fontSize: 14,
  mb: 1,
};

const infoBoxSx = {
  p: 1.25,
  borderRadius: 12,
  border: `1px solid ${SUB_BORDER}`,
  background: alpha("#0e1a2b", 0.7),
};





const dangerBtnSx = {
  height: 40,
  borderRadius: 12,
  fontWeight: 900,
  textTransform: "none" as const,
  color: "#ffb4b4",
  border: "1px solid rgba(255,120,120,.22)",
  background: "rgba(255,90,90,.08)",
  "&:hover": {
    background: "rgba(255,90,90,.14)",
    borderColor: "rgba(255,120,120,.35)",
  },
};

const AccountSettingsModal: React.FC<AccountSettingsModalProps> = ({
  open,
  onClose,
  userEmail,
  userPhone = "",
  marketingEmailAgree = false,
  marketingSmsAgree = false,
  connectedProviders,
  loading = false,
  error = null,
  successMessage = null,
  onSave,
  onOpenPasswordChange,
  onWithdraw,
  onOpenEmailChange,
  onConnectSocial,
  onDisconnectSocial,
  socialLoadingProvider = null,
}) => {
  const [phone, setPhone] = useState(userPhone);
  const [marketingEmail, setMarketingEmail] = useState(marketingEmailAgree);
  const [marketingSms, setMarketingSms] = useState(marketingSmsAgree);

  const [phoneErr, setPhoneErr] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhone(userPhone || "");
    setMarketingEmail(!!marketingEmailAgree);
    setMarketingSms(!!marketingSmsAgree);
    setPhoneErr(null);
    setDirty(false);
  }, [open, userPhone, marketingEmailAgree, marketingSmsAgree]);

  const normalizedPhone = useMemo(() => normalizePhone(phone), [phone]);

  const hasChanges = useMemo(() => {
    return (
      normalizePhone(userPhone || "") !== normalizedPhone ||
      !!marketingEmailAgree !== marketingEmail ||
      !!marketingSmsAgree !== marketingSms
    );
  }, [userPhone, normalizedPhone, marketingEmailAgree, marketingSmsAgree, marketingEmail, marketingSms]);

  const handleChangePhone = (value: string) => {
    setPhone(value);
    setDirty(true);
    if (value && !isLikelyKRPhone(value)) {
      setPhoneErr("휴대폰 번호 형식이 올바르지 않습니다. (예: 01012345678)");
    } else {
      setPhoneErr(null);
    }
  };

  const handleToggleEmailMarketing = (checked: boolean) => {
    setMarketingEmail(checked);
    setDirty(true);
  };

  const handleToggleSmsMarketing = (checked: boolean) => {
    setMarketingSms(checked);
    setDirty(true);
  };

  const handleSave = () => {
    const trimmedPhone = safeTrim(phone);
    const normalized = normalizePhone(trimmedPhone);

    if (trimmedPhone && !isLikelyKRPhone(normalized)) {
      setPhoneErr("휴대폰 번호 형식이 올바르지 않습니다. (예: 01012345678)");
      return;
    }

    setPhoneErr(null);

    onSave?.({
      phone: normalized,
      marketingEmailAgree: marketingEmail,
      marketingSmsAgree: marketingSms,
    });
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ elevation: 0, sx: { borderRadius: 0, background: "transparent" } }}
      BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,.55)" } }}
    >
      <Card>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography sx={{ color: TEXT_MAIN, fontWeight: 900, fontSize: 18 }}>
            계정 설정
          </Typography>
          <IconButton onClick={onClose} sx={{ color: TEXT_SUB }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {successMessage}
          </Alert>
        )}

        <DialogContent sx={{ px: 0, pt: 0.5, pb: 0 }}>
          <Box sx={{ display: "grid", gap: 2 }}>
            <Box>
              <Typography sx={sectionTitleSx}>기본 계정 정보</Typography>

              <Box sx={{ display: "grid", gap: 1 }}>
                <TextField
                  fullWidth
                  label="이메일"
                  value={userEmail}
                  sx={fieldSx}
                  disabled
                  InputProps={{ readOnly: true }}
                  helperText="현재 이메일은 읽기 전용입니다."
                />

                <TextField
                  fullWidth
                  label="휴대폰 번호"
                  placeholder="01012345678"
                  value={phone}
                  onChange={(e) => handleChangePhone(e.target.value)}
                  sx={fieldSx}
                  disabled={loading}
                  error={!!phoneErr}
                  helperText={
                    phoneErr ||
                    "계정 보호, 중요 알림, 아이디 찾기 및 고객지원 대응을 위해 권장됩니다."
                  }
                />

                <Box
                  sx={{
                    mt: 1,
                    ...infoBoxSx,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography sx={{ color: TEXT_MAIN, fontWeight: 800, fontSize: 13 }}>
                      이메일 변경
                    </Typography>
                    <Typography sx={{ color: TEXT_SUB, fontSize: 12 }}>
                      새 이메일로 인증 절차를 진행한 뒤 변경하는 방식이 안전합니다.
                    </Typography>
                  </Box>

                  <Button
                    sx={{ ...btnOutline, minWidth: 116, height: 40 }}
                    onClick={onOpenEmailChange}
                    disabled={loading || !onOpenEmailChange}
                  >
                    변경하기
                  </Button>
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 1,
                  ...infoBoxSx,
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                }}
              >
                <PhoneIphoneRoundedIcon sx={{ color: "#8ec5ff", mt: "2px", fontSize: 18 }} />
                <Typography sx={{ color: TEXT_SUB, fontSize: 12, lineHeight: 1.55 }}>
                  현재 단계에서는 이메일은 읽기 전용으로 두고, 휴대폰과 수신동의만 수정 가능하게 분리하는 것이 안전합니다.
                  이메일 변경은 인증·중복검사·재로그인 처리가 함께 필요하므로 별도 절차로 분리하는 것이 맞습니다.
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderColor: SUB_BORDER }} />

            <Box>
              <Typography sx={sectionTitleSx}>알림 및 마케팅 수신 설정</Typography>

              <Box sx={{ display: "grid", gap: 0.5, color: TEXT_MAIN }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={marketingEmail}
                      onChange={(e) => handleToggleEmailMarketing(e.target.checked)}
                      sx={{ color: TEXT_SUB }}
                      disabled={loading}
                    />
                  }
                  label="광고성 정보 수신 동의 - 이메일"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={marketingSms}
                      onChange={(e) => handleToggleSmsMarketing(e.target.checked)}
                      sx={{ color: TEXT_SUB }}
                      disabled={loading}
                    />
                  }
                  label="광고성 정보 수신 동의 - 문자메시지"
                />

                <Typography sx={{ color: TEXT_SUB, fontSize: 12, ml: 0.5, mt: 0.25, lineHeight: 1.55 }}>
                  이벤트, 혜택, 신규 서비스 및 프로모션 안내에 사용됩니다.
                  동의하지 않아도 서비스 이용에는 제한이 없으며, 언제든지 변경할 수 있습니다.
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderColor: SUB_BORDER }} />

            <Box>
              <Typography sx={sectionTitleSx}>보안</Typography>

              <Box sx={{ display: "grid", gap: 1 }}>
                <Box
                  sx={{
                    ...infoBoxSx,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LockRoundedIcon sx={{ color: "#8ec5ff", fontSize: 18 }} />
                    <Box>
                      <Typography sx={{ color: TEXT_MAIN, fontWeight: 800, fontSize: 13 }}>
                        비밀번호 변경
                      </Typography>
                      <Typography sx={{ color: TEXT_SUB, fontSize: 12 }}>
                        현재 비밀번호 확인 후 새 비밀번호로 변경합니다.
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    sx={{ ...btnOutline, minWidth: 116, height: 40 }}
                    onClick={onOpenPasswordChange}
                    disabled={loading}
                  >
                    변경하기
                  </Button>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ borderColor: SUB_BORDER }} />

            <Divider sx={{ borderColor: SUB_BORDER }} />

            <Box>
              <Typography sx={sectionTitleSx}>계정 관리</Typography>

              <Box
                sx={{
                  ...infoBoxSx,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography sx={{ color: TEXT_MAIN, fontWeight: 800, fontSize: 13 }}>
                    회원탈퇴
                  </Typography>
                  <Typography sx={{ color: TEXT_SUB, fontSize: 12, lineHeight: 1.55 }}>
                    탈퇴는 구독, 결제, 환불, 보관정책 확인이 함께 필요하므로 최종 확인 절차를 거쳐야 합니다.
                  </Typography>
                </Box>

                <Button
                  startIcon={<DeleteOutlineRoundedIcon />}
                  sx={dangerBtnSx}
                  onClick={onWithdraw}
                  disabled={loading || !onWithdraw}
                >
                  탈퇴하기
                </Button>
              </Box>
            </Box>
          </Box>

          {dirty && !hasChanges && !phoneErr && (
            <FormHelperText sx={{ mt: 1.5, ml: 0, color: TEXT_SUB }}>
              변경된 내용이 없습니다.
            </FormHelperText>
          )}

          <Divider sx={{ my: 2, borderColor: SUB_BORDER }} />

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={onClose} disabled={loading} fullWidth sx={btnOutline}>
              취소
            </Button>

            <Button
              onClick={handleSave}
              disabled={loading || !!phoneErr || !hasChanges}
              fullWidth
              sx={btnPrimary}
            >
              저장하기
            </Button>
          </Box>
        </DialogContent>
      </Card>
    </Dialog>
  );
};

export default AccountSettingsModal;